import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { apiService } from '../../services/api'
import {
  Globe,
  Plus,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle,
  ExternalLink,
  Copy,
  Shield,
  Trash2,
  RefreshCw,
  Info,
  BookOpen,
  X,
  Calendar,
  Zap,
  AlertTriangle,
  Check,
  Terminal,
  Star,
  HelpCircle,
  ChevronRight
} from 'lucide-react'
import toast from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'

const DomainManagement = () => {
  const [showAddDomain, setShowAddDomain] = useState(false)
  const [selectedDomain, setSelectedDomain] = useState(null)
  const [showInstructions, setShowInstructions] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [domainToDelete, setDomainToDelete] = useState(null)
  const queryClient = useQueryClient()

  // Fetch domains
  const { data: domainsResponse, isLoading, error } = useQuery(
    'client-domains',
    () => apiService.get('/domains').then(res => res.data),
    {
      staleTime: 30000,
      refetchInterval: 60000,
      retry: (failureCount, error) => {
        // Don't retry on 401 errors (authentication issues)
        if (error?.response?.status === 401) {
          return false
        }
        // Allow up to 2 retries for other errors
        return failureCount < 2
      }
    }
  )

  // Extract domains array from response
  const domains = domainsResponse?.domains || []

  // Add domain mutation
  const addDomainMutation = useMutation(
    (data) => apiService.post('/domains', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('client-domains')
        setShowAddDomain(false)
        toast.success('Domain added successfully!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to add domain')
      }
    }
  )

  // Verify domain mutation
  const verifyDomainMutation = useMutation(
    (domainId) => apiService.post(`/domains/${domainId}/verify`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('client-domains')
        toast.success('Domain verification initiated!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to verify domain')
      }
    }
  )

  // Set primary domain mutation
  const setPrimaryMutation = useMutation(
    (domainId) => apiService.patch(`/domains/${domainId}/set-primary`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('client-domains')
        toast.success('Primary domain updated!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to set primary domain')
      }
    }
  )

  // Delete domain mutation
  const deleteDomainMutation = useMutation(
    (domainId) => apiService.delete(`/domains/${domainId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('client-domains')
        setShowDeleteConfirm(false)
        setDomainToDelete(null)
        toast.success('Domain removed successfully!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to remove domain')
      }
    }
  )

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
      case 'active':
        return <CheckCircle className="h-4 w-4 text-success-500" />
      case 'pending':
        return <Clock className="h-4 w-4 text-accent-500" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-error-500" />
      default:
        return <Clock className="h-4 w-4 text-neutral-500" />
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'verified':
      case 'active':
        return 'Active'
      case 'pending':
        return 'Pending Setup'
      case 'failed':
        return 'Verification Failed'
      default:
        return 'Unknown'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
      case 'active':
        return 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300'
      case 'pending':
        return 'bg-accent-100 text-accent-800 dark:bg-accent-900/20 dark:text-accent-300'
      case 'failed':
        return 'bg-error-100 text-error-800 dark:bg-error-900/20 dark:text-error-300'
      default:
        return 'bg-neutral-100 text-neutral-800 dark:bg-[#201d2e] dark:text-neutral-300'
    }
  }

  const getDisplayDomain = (fullDomain) => {
    if (fullDomain.length > 30) {
      return fullDomain.substring(0, 27) + '...'
    }
    return fullDomain
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-neutral-600 dark:text-neutral-400">Loading your domains...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="border-error-200 dark:border-error-800">
          <CardContent className="text-center py-16 px-8">
            <div className="w-16 h-16 mx-auto mb-6 bg-error-100 dark:bg-error-900/20 rounded-full flex items-center justify-center">
              <XCircle className="h-8 w-8 text-error-600 dark:text-error-400" />
            </div>
            <h3 className="text-xl font-semibold text-error-900 dark:text-error-100 mb-3">
              Error Loading Domains
            </h3>
            <p className="text-error-700 dark:text-error-300 mb-6">
              {error.response?.data?.error || 'Failed to load domains'}
            </p>
            <Button 
              onClick={() => window.location.reload()} 
              variant="outline" 
              className="border-error-300 dark:border-error-600"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    )
  }

  // Domain statistics
  const stats = domains.length > 0 ? {
    total: domains.length,
    verified: domains.filter(d => d.status === 'verified' || d.status === 'active').length,
    pending: domains.filter(d => d.status === 'pending').length
  } : null

  return (
    <div className="pt-4 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
            Custom Domains
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">
            Connect your own domain to give your website a professional edge
          </p>
        </div>
        
        <Button 
          onClick={() => setShowAddDomain(true)}
          className="btn-primary shadow-lg hover:shadow-xl transition-all duration-200"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Domain
        </Button>
      </motion.div>

      {/* Statistics */}
      {stats && stats.total > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          <StatCard
            title="Total Domains"
            value={stats.total}
            icon={Globe}
            color="primary"
            index={0}
          />
          <StatCard
            title="Verified"
            value={stats.verified}
            icon={CheckCircle}
            color="success"
            index={1}
          />
          <StatCard
            title="Pending"
            value={stats.pending}
            icon={Clock}
            color="accent"
            index={2}
          />
        </motion.div>
      )}

      {/* Domains Grid */}
      {domains.length > 0 ? (
        <motion.div
          className="grid gap-6 md:grid-cols-2 xl:grid-cols-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, staggerChildren: 0.1 }}
        >
          {domains.map((domain, index) => (
            <motion.div
              key={domain.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <DomainCard
                domain={domain}
                onVerify={() => verifyDomainMutation.mutate(domain.id)}
                onSetPrimary={() => setPrimaryMutation.mutate(domain.id)}
                onDelete={() => {
                  setDomainToDelete(domain)
                  setShowDeleteConfirm(true)
                }}
                onShowInstructions={() => {
                  setSelectedDomain(domain)
                  setShowInstructions(true)
                }}
                isVerifying={verifyDomainMutation.isLoading}
                isSettingPrimary={setPrimaryMutation.isLoading}
                isDeleting={deleteDomainMutation.isLoading}
                getStatusIcon={getStatusIcon}
                getStatusText={getStatusText}
                getStatusColor={getStatusColor}
                getDisplayDomain={getDisplayDomain}
              />
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="bg-white dark:bg-[#1a1625] border-neutral-200 dark:border-[#2f2b41]">
            <CardContent className="text-center py-20 px-8">
              <div className="w-20 h-20 mx-auto mb-6 bg-primary-100 dark:bg-primary-900/20 rounded-2xl flex items-center justify-center">
                <Globe className="w-10 h-10 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-2xl font-bold text-neutral-900 dark:text-white mb-4">
                No domains yet
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400 mb-8 max-w-md mx-auto">
                Add your first custom domain to get started. We'll guide you through the setup process step by step.
              </p>
              <Button 
                onClick={() => setShowAddDomain(true)}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Domain
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showAddDomain && (
          <AddDomainModal
            onClose={() => setShowAddDomain(false)}
            onSubmit={(data) => addDomainMutation.mutate(data)}
            isLoading={addDomainMutation.isLoading}
          />
        )}

        {showInstructions && selectedDomain && (
          <DomainInstructionsModal
            domain={selectedDomain}
            onClose={() => {
              setShowInstructions(false)
              setSelectedDomain(null)
            }}
          />
        )}

        {showDeleteConfirm && domainToDelete && (
          <DeleteConfirmationModal
            domain={domainToDelete}
            onClose={() => {
              setShowDeleteConfirm(false)
              setDomainToDelete(null)
            }}
            onConfirm={() => deleteDomainMutation.mutate(domainToDelete.id)}
            isDeleting={deleteDomainMutation.isLoading}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
  >
    <Card className="bg-white dark:bg-[#1a1625] border-neutral-200 dark:border-[#2f2b41] hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-6">
        <div className="flex items-center">
          <div className={cn(
            "flex-shrink-0 p-3 rounded-xl mr-4",
            color === 'primary' && "bg-primary-100 dark:bg-primary-900/20",
            color === 'success' && "bg-success-100 dark:bg-success-900/20",
            color === 'accent' && "bg-accent-100 dark:bg-accent-900/20",
            color === 'error' && "bg-error-100 dark:bg-error-900/20"
          )}>
            <Icon className={cn(
              "h-6 w-6",
              color === 'primary' && "text-primary-600 dark:text-primary-400",
              color === 'success' && "text-success-600 dark:text-success-400",
              color === 'accent' && "text-accent-600 dark:text-accent-400",
              color === 'error' && "text-error-600 dark:text-error-400"
            )} />
          </div>
          <div>
            <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {value}
            </div>
            <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
              {title}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
)

// Domain Card Component
const DomainCard = ({ domain, onVerify, onSetPrimary, onDelete, onShowInstructions, isVerifying, isSettingPrimary, isDeleting, getStatusIcon, getStatusText, getStatusColor, getDisplayDomain }) => {
  const fullDomain = domain.subdomain ? `${domain.subdomain}.${domain.domain}` : domain.domain
  const isActive = domain.status === 'active' || domain.status === 'verified'
  const isAssigned = domain.verification_method === 'none'

  return (
    <Card className={cn(
      "group hover:shadow-xl transition-all duration-300 bg-white dark:bg-[#1a1625] border-neutral-200 dark:border-[#2f2b41] overflow-hidden",
      domain.is_primary && "ring-2 ring-accent-600 dark:ring-accent-500"
    )}>
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 flex-shrink-0 relative">
              <Globe className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              {domain.is_primary && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-accent-600 rounded-full flex items-center justify-center">
                  <Star className="w-3 h-3 text-white fill-white" />
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-neutral-900 dark:text-white text-lg group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors break-all">
                {getDisplayDomain(fullDomain)}
              </h3>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className={cn("inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium", getStatusColor(domain.status))}>
                  {getStatusIcon(domain.status)}
                  {getStatusText(domain.status)}
                </span>
                {domain.is_primary && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800 dark:bg-accent-900/20 dark:text-accent-300">
                    <Star className="w-3 h-3 fill-current" />
                    Primary
                  </span>
                )}
                {!!domain.ssl_enabled && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300">
                    <Shield className="w-3 h-3" />
                    SSL
                  </span>
                )}
                {isActive && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
                    <Zap className="w-3 h-3" />
                    Live
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Remove — icon only, top-right, hidden for assigned subdomain */}
          {!isAssigned && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              disabled={isDeleting}
              title="Remove domain"
              className="text-error-400 hover:text-error-600 dark:hover:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 ml-2 shrink-0 p-2"
            >
              {isDeleting ? <LoadingSpinner size="sm" /> : <Trash2 className="w-4 h-4" />}
            </Button>
          )}
        </div>


        {/* Error Message */}
        {domain.error_message && (
          <div className="mb-6 p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-error-600 dark:text-error-400 mt-0.5" />
              <div>
                <p className="font-medium text-error-900 dark:text-error-100 mb-1">Configuration Error</p>
                <p className="text-sm text-error-700 dark:text-error-300">{domain.error_message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Domain Info */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
            <Calendar className="w-4 h-4" />
            <span>Added {new Date(domain.created_at).toLocaleDateString()}</span>
          </div>
          {domain.verified_at && (
            <div className="flex items-center gap-2 text-sm text-success-600 dark:text-success-400">
              <CheckCircle className="w-4 h-4" />
              <span>Verified {new Date(domain.verified_at).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-4 border-t border-neutral-100 dark:border-[#2f2b41]">
          <Button
            variant="ghost"
            size="sm"
            onClick={onShowInstructions}
            className="text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Setup Guide
          </Button>
          
          {(domain.status === 'pending' || domain.status === 'failed') && (
            <Button
              variant="outline"
              size="sm"
              onClick={onVerify}
              disabled={isVerifying}
              className="hover:bg-accent-50 dark:hover:bg-accent-900/20 border-accent-200 dark:border-accent-700"
            >
              {isVerifying ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Verify
                </>
              )}
            </Button>
          )}

          {isActive && !domain.is_primary && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onSetPrimary}
              disabled={isSettingPrimary}
              className="text-accent-600 dark:text-accent-400 hover:text-accent-700 dark:hover:text-accent-300 hover:bg-accent-50 dark:hover:bg-accent-900/20"
            >
              {isSettingPrimary ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <Star className="w-4 h-4 mr-2" />
                  Set as Primary
                </>
              )}
            </Button>
          )}

        </div>
      </CardContent>
    </Card>
  )
}

// Add Domain Modal Component
const AddDomainModal = ({ onClose, onSubmit, isLoading }) => {
  const [domain, setDomain] = useState('')
  const [subdomain, setSubdomain] = useState('')
  const [useSubdomain, setUseSubdomain] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!domain.trim()) return

    const data = {
      domain: domain.toLowerCase()
    }
    
    if (useSubdomain && subdomain.trim()) {
      data.subdomain = subdomain.toLowerCase()
    }

    onSubmit(data)
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60" 
          onClick={onClose} 
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-lg"
        >
          <Card className="border-0 shadow-2xl bg-white dark:bg-[#1a1625]">
            <form onSubmit={handleSubmit}>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-xl flex items-center justify-center">
                      <Globe className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-neutral-900 dark:text-white">Add Custom Domain</CardTitle>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">Connect your domain to your website</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="text-neutral-400 hover:text-neutral-600"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Domain Name
                    </label>
                    <input
                      type="text"
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                      placeholder="example.com"
                      className="w-full px-4 py-3 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                      required
                    />
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="useSubdomain"
                      checked={useSubdomain}
                      onChange={(e) => setUseSubdomain(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="useSubdomain" className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                      Use subdomain
                    </label>
                  </div>

                  {useSubdomain && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Subdomain
                      </label>
                      <div className="flex items-center border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus-within:ring-2 focus-within:ring-accent-500 focus-within:border-transparent">
                        <input
                          type="text"
                          value={subdomain}
                          onChange={(e) => setSubdomain(e.target.value)}
                          placeholder="www"
                          className="flex-1 px-4 py-3 border-0 rounded-l-lg focus:outline-none focus:ring-0 bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                        />
                        <div className="px-4 py-3 bg-neutral-100 dark:bg-[#201d2e] rounded-r-lg text-neutral-600 dark:text-neutral-300 font-medium border-l border-neutral-300 dark:border-[#2f2b41]">
                          .{domain || 'domain.com'}
                        </div>
                      </div>
                      {subdomain && domain && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-300 mt-1">
                          Full domain: <span className="font-medium">{subdomain}.{domain}</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-primary-900 dark:text-primary-100 mb-1">
                        What happens next?
                      </p>
                      <p className="text-sm text-primary-700 dark:text-primary-300">
                        After adding your domain, we'll provide DNS configuration instructions to complete the setup.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading || !domain.trim()}
                    className="flex-1 btn-primary"
                  >
                    {isLoading ? <LoadingSpinner size="sm" /> : 'Add Domain'}
                  </Button>
                </div>
              </CardContent>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}

// Domain Instructions Modal Component - Comprehensive Tutorial
const DomainInstructionsModal = ({ domain, onClose }) => {
  const fullDomain = domain.subdomain ? `${domain.subdomain}.${domain.domain}` : domain.domain
  const [activeStep, setActiveStep] = useState(1)
  const [completedSteps, setCompletedSteps] = useState(new Set())

  const toggleStepComplete = (stepNumber) => {
    const newCompleted = new Set(completedSteps)
    if (newCompleted.has(stepNumber)) {
      newCompleted.delete(stepNumber)
    } else {
      newCompleted.add(stepNumber)
    }
    setCompletedSteps(newCompleted)
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard!')
  }

  const usingSaaS = !!(domain.cf_fallback_hostname && domain.cf_ownership_txt_name)

  const dnsRecords = usingSaaS ? [
    {
      type: 'CNAME',
      name: domain.subdomain || '@',
      value: domain.cf_fallback_hostname,
      description: 'Points your domain through Cloudflare — SSL is handled automatically'
    },
    {
      type: 'TXT',
      name: domain.cf_ownership_txt_name,
      value: domain.cf_ownership_txt_value,
      description: 'Verifies ownership with Cloudflare — can be removed after verification'
    }
  ] : [
    {
      type: 'A',
      name: domain.subdomain || '@',
      value: '51.104.28.89',
      description: domain.subdomain
        ? `Points ${domain.subdomain}.${domain.domain} to our hosting server`
        : 'Points your root domain to our hosting server'
    },
    {
      type: 'TXT',
      name: '_webnest-verify',
      value: `webnest-verify=${domain.verification_token}`,
      description: 'Verifies domain ownership — can be removed after verification'
    }
  ]

  const steps = [
    {
      number: 1,
      title: 'Access Your DNS Settings',
      content: (
        <div className="space-y-4">
          <p className="text-neutral-700 dark:text-neutral-300">
            Log into your domain provider's control panel. Common providers include:
          </p>
          <div className="grid grid-cols-2 gap-4">
            {['GoDaddy', 'Namecheap', 'Cloudflare', 'Route 53', 'Google Domains', 'Squarespace'].map(provider => (
              <div key={provider} className="flex items-center gap-2 p-3 bg-neutral-50 dark:bg-[#201d2e] rounded-lg">
                <Globe className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                <span className="text-sm font-medium text-neutral-900 dark:text-white">{provider}</span>
              </div>
            ))}
          </div>
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-primary-900 dark:text-primary-100 mb-1">
                  Need help finding DNS settings?
                </p>
                <p className="text-sm text-primary-700 dark:text-primary-300">
                  Look for sections labeled "DNS", "DNS Management", "Name Servers", or "Advanced Settings"
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 2,
      title: 'Add DNS Records',
      content: (
        <div className="space-y-4">
          <p className="text-neutral-700 dark:text-neutral-300">
            Add the following DNS records exactly as shown:
          </p>
          <div className="space-y-4">
            {dnsRecords.map((record, index) => (
              <div key={index} className="bg-neutral-50 dark:bg-[#201d2e] rounded-lg p-4 border border-neutral-200 dark:border-[#2f2b41]">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Type</label>
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-sm font-semibold text-neutral-900 dark:text-white bg-neutral-100 dark:bg-[#221f2f] px-2 py-1 rounded">
                        {record.type}
                      </code>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Name/Host</label>
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-sm text-neutral-900 dark:text-white">{record.name}</code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(record.name)}
                        className="text-primary-600 dark:text-primary-400 p-1"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Value/Points to</label>
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-sm text-neutral-900 dark:text-white flex-1 break-all">{record.value}</code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(record.value)}
                        className="text-primary-600 dark:text-primary-400 p-1"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-2">{record.description}</p>
              </div>
            ))}
          </div>
          <div className="bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-accent-600 dark:text-accent-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-accent-900 dark:text-accent-100 mb-1">
                  Important Notes
                </p>
                <ul className="text-sm text-accent-700 dark:text-accent-300 space-y-1">
                  <li>• Remove any existing A or CNAME records for the same name</li>
                  <li>• TTL (Time to Live) can be set to 300 seconds for faster updates</li>
                  <li>• Some providers use "@" for the root domain instead of leaving it blank</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 3,
      title: 'Wait for Propagation',
      content: (
        <div className="space-y-4">
          <p className="text-neutral-700 dark:text-neutral-300">
            DNS changes need time to propagate across the internet. This process typically takes:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-success-50 dark:bg-success-900/20 rounded-lg border border-success-200 dark:border-success-800">
              <Clock className="w-8 h-8 text-success-600 dark:text-success-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-success-900 dark:text-success-100">5-15 min</div>
              <div className="text-sm text-success-700 dark:text-success-300">Typical</div>
            </div>
            <div className="text-center p-4 bg-accent-50 dark:bg-accent-900/20 rounded-lg border border-accent-200 dark:border-accent-800">
              <Clock className="w-8 h-8 text-accent-600 dark:text-accent-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-accent-900 dark:text-accent-100">1-4 hours</div>
              <div className="text-sm text-accent-700 dark:text-accent-300">Common</div>
            </div>
            <div className="text-center p-4 bg-error-50 dark:bg-error-900/20 rounded-lg border border-error-200 dark:border-error-800">
              <Clock className="w-8 h-8 text-error-600 dark:text-error-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-error-900 dark:text-error-100">24-48 hours</div>
              <div className="text-sm text-error-700 dark:text-error-300">Maximum</div>
            </div>
          </div>
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Terminal className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-primary-900 dark:text-primary-100 mb-2">
                  Check propagation status
                </p>
                <p className="text-sm text-primary-700 dark:text-primary-300 mb-2">
                  You can check if your DNS changes have propagated using these tools:
                </p>
                <div className="flex flex-wrap gap-2">
                  <code className="text-xs bg-primary-100 dark:bg-primary-800 px-2 py-1 rounded text-primary-900 dark:text-primary-100">
                    whatsmydns.net
                  </code>
                  <code className="text-xs bg-primary-100 dark:bg-primary-800 px-2 py-1 rounded text-primary-900 dark:text-primary-100">
                    dnschecker.org
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 4,
      title: 'Verify Your Domain',
      content: (
        <div className="space-y-4">
          <p className="text-neutral-700 dark:text-neutral-300">
            Once DNS propagation is complete, click the "Verify" button on your domain card to complete the setup.
          </p>
          <div className="bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-success-900 dark:text-success-100 mb-1">
                  What happens after verification?
                </p>
                <ul className="text-sm text-success-700 dark:text-success-300 space-y-1">
                  <li>• Your domain will be marked as Active</li>
                  <li>• SSL (HTTPS) is automatically provisioned via Cloudflare</li>
                  <li>• Your website will be live on your custom domain</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-accent-600 dark:text-accent-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-accent-900 dark:text-accent-100 mb-1">
                  Troubleshooting
                </p>
                <p className="text-sm text-accent-700 dark:text-accent-300">
                  If verification fails, double-check your DNS records and wait a bit longer for propagation.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ]

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60" 
          onClick={onClose} 
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-4xl"
        >
          <Card className="border-0 shadow-2xl bg-white dark:bg-[#1a1625]">
            <CardHeader className="pb-4 border-b border-neutral-200 dark:border-[#2f2b41]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-xl flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <CardTitle className="text-xl text-neutral-900 dark:text-white">Domain Setup Guide</CardTitle>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      Step-by-step instructions for <strong>{fullDomain}</strong>
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="p-6">
              {/* Progress Steps */}
              <div className="mb-8">
                {/* Progress Bar Background */}
                <div className="relative">
                  <div className="absolute top-6 left-0 w-full h-0.5 bg-neutral-200 dark:bg-[#201d2e]" />
                  <div 
                    className="absolute top-6 left-0 h-0.5 bg-gradient-to-r from-accent-500 to-primary-500 transition-all duration-500 ease-out"
                    style={{ 
                      width: `${((activeStep - 1) / (steps.length - 1)) * 100}%` 
                    }}
                  />
                  
                  {/* Step Indicators */}
                  <div className="flex justify-between">
                    {steps.map((step, index) => {
                      const isCompleted = completedSteps.has(step.number)
                      const isActive = activeStep === step.number
                      const isPast = step.number < activeStep
                      
                      return (
                        <div key={step.number} className="flex flex-col items-center">
                          <button
                            onClick={() => setActiveStep(step.number)}
                            className={cn(
                              "w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 shadow-lg hover:shadow-xl relative z-10",
                              isCompleted
                                ? "bg-gradient-to-br from-success-500 to-success-600 text-white hover:from-success-600 hover:to-success-700 ring-4 ring-success-500/20"
                                : isActive
                                ? "bg-gradient-to-br from-accent-500 to-primary-500 text-white hover:from-accent-600 hover:to-primary-600 ring-4 ring-accent-500/30 scale-110"
                                : isPast
                                ? "bg-neutral-300 dark:bg-[#201d2e] text-neutral-600 dark:text-neutral-300 hover:bg-neutral-400 dark:hover:bg-[#221f2f] border-2 border-neutral-400 dark:border-[#2f2b41]"
                                : "bg-white dark:bg-[#201d2e] text-neutral-500 dark:text-neutral-400 border-2 border-neutral-300 dark:border-[#2f2b41] hover:border-accent-300 dark:hover:border-accent-600 hover:text-accent-600"
                            )}
                          >
                            {isCompleted ? (
                              <Check className="w-5 h-5" />
                            ) : (
                              <span className={cn(
                                "font-bold",
                                isActive && "text-white"
                              )}>
                                {step.number}
                              </span>
                            )}
                          </button>
                          
                          {/* Step Label */}
                          <div className="mt-3 text-center max-w-20">
                            <p className={cn(
                              "text-xs font-medium transition-colors duration-200",
                              isActive 
                                ? "text-accent-600 dark:text-accent-400" 
                                : isCompleted
                                ? "text-success-600 dark:text-success-400"
                                : "text-neutral-500 dark:text-neutral-400"
                            )}>
                              {step.title.split(' ').slice(0, 2).join(' ')}
                            </p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
                
                {/* Current Step Title */}
                <div className="mt-6 text-center">
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    Step {activeStep} of {steps.length}
                  </p>
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mt-1">
                    {steps.find(s => s.number === activeStep)?.title}
                  </h3>
                </div>
              </div>

              {/* Active Step Content */}
              <div className="min-h-96">
                {steps.map(step => (
                  <div key={step.number} className={cn("space-y-6", activeStep !== step.number && "hidden")}>
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-bold text-neutral-900 dark:text-white">
                        Step {step.number}: {step.title}
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleStepComplete(step.number)}
                        className={cn(
                          completedSteps.has(step.number)
                            ? "border-success-500 text-success-600 dark:text-success-400"
                            : "border-neutral-300 dark:border-[#2f2b41]"
                        )}
                      >
                        {completedSteps.has(step.number) ? (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Completed
                          </>
                        ) : (
                          'Mark Complete'
                        )}
                      </Button>
                    </div>
                    <div className="prose prose-neutral dark:prose-invert max-w-none">
                      {step.content}
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between pt-6 border-t border-neutral-200 dark:border-[#2f2b41]">
                <Button
                  variant="outline"
                  onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
                  disabled={activeStep === 1}
                >
                  Previous
                </Button>
                <div className="text-sm text-neutral-600 dark:text-neutral-400">
                  Step {activeStep} of {steps.length}
                </div>
                {activeStep === steps.length ? (
                  <Button onClick={onClose}>
                    Finish
                    <Check className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={() => setActiveStep(Math.min(steps.length, activeStep + 1))}>
                    Next
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ domain, onClose, onConfirm, isDeleting }) => {
  const fullDomain = domain.subdomain ? `${domain.subdomain}.${domain.domain}` : domain.domain

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60" 
          onClick={onClose} 
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-md"
        >
          <Card className="border-0 shadow-2xl bg-white dark:bg-[#1a1625]">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-error-100 dark:bg-error-900/20 rounded-xl flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-error-600 dark:text-error-400" />
                  </div>
                  <CardTitle className="text-xl text-error-900 dark:text-error-100">Remove Domain</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="text-center">
                <p className="text-neutral-700 dark:text-neutral-300 mb-4">
                  Are you sure you want to remove this domain?
                </p>
                
                <div className="bg-neutral-50 dark:bg-[#201d2e] border border-neutral-200 dark:border-[#2f2b41] rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center gap-3">
                    <Globe className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                    <span className="font-medium text-neutral-900 dark:text-white">{fullDomain}</span>
                  </div>
                </div>

                <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-4">
                  <p className="text-sm text-error-700 dark:text-error-300">
                    This action cannot be undone. The domain will be permanently removed from your account.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={onConfirm}
                  disabled={isDeleting}
                  className="flex-1 bg-error-600 hover:bg-error-700 text-white border-0"
                >
                  {isDeleting ? <LoadingSpinner size="sm" /> : 'Remove Domain'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}

export default DomainManagement